import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, FileText, Table, AlertTriangle, CheckCircle2, Save, Upload } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const download = async (url, fallbackName) => {
  const r = await api.get(url, { responseType: "blob" });
  const name = /filename="?([^";]+)"?/.exec(r.headers["content-disposition"] || "")?.[1] || fallbackName;
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r.data); a.download = name; a.click();
  URL.revokeObjectURL(a.href);
};

export function LeagueExportTab() {
  const [leagues, setLeagues] = useState([]);
  const [fields, setFields] = useState([]);
  const [leagueId, setLeagueId] = useState("");
  const [data, setData] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/leagues").then(r => {
      const ls = r.data.filter(l => l.active !== false);
      setLeagues(ls);
      const cml = ls.find(l => /mini/i.test(l.name || ""));
      setLeagueId(cml?.id || ls[0]?.id || "");
    }).catch(() => {});
    api.get("/admin/league-export/fields").then(r => setFields(r.data)).catch(() => {});
  }, []);
  const load = () => leagueId && api.get(`/admin/league-export/${leagueId}`).then(r => { setData(r.data); setCfg(r.data.config); }).catch(() => setData(null));
  useEffect(() => { setData(null); load(); }, [leagueId]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveCfg = async () => {
    setBusy(true);
    try { await api.put(`/admin/league-export/${leagueId}/config`, cfg); toast.success("Export settings saved"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Couldn't save"); }
    finally { setBusy(false); }
  };
  const toggleField = (k) => setCfg(c => ({ ...c, fields: c.fields.includes(k) ? c.fields.filter(f => f !== k) : fields.map(f => f.key).filter(f => f === k || c.fields.includes(f)) }));
  const get = async (kind) => { try { await download(`/admin/league-export/${leagueId}/${kind}`, `team-info.${kind}`); } catch { toast.error("Download failed"); } };

  const shown = fields.filter(f => cfg?.fields?.includes(f.key));
  return (
    <div className="space-y-6" data-testid="league-export-tab">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Leagues</div>
          <h2 className="font-display font-black text-3xl mt-1">League team info form</h2>
          <p className="opacity-60 text-sm mt-1">Builds the "Team and Ground Information" return for a league from the teams, coaches and venues you already hold. Check the readiness table, then download the filled-in Word form or a CSV to attach to your email.</p>
        </div>
        <div className="w-72">
          <Label className="text-[10px] tracking-widest uppercase opacity-70">League</Label>
          <Select value={leagueId} onValueChange={setLeagueId}>
            <SelectTrigger data-testid="league-export-league" className="mt-1.5"><SelectValue placeholder="Choose a league" /></SelectTrigger>
            <SelectContent>{leagues.map(l => <SelectItem key={l.id} value={l.id}>{l.name}{l.division ? ` · ${l.division}` : ""}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {data && cfg && (
        <>
          <div className="card-tactical rounded-md p-5 space-y-4" data-testid="league-export-config">
            <div className="text-[10px] tracking-widest uppercase text-[#83B68B]">Form contents for {data.league.name}</div>
            <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="league-export-template">
              {data.template?.kind
                ? <span className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[#83B68B]/50 text-[#83B68B]"><FileText className="w-3 h-3" /> Filling the league's own form: {data.template.name}</span>
                : <span className="opacity-60">No league form on file — a plain layout will be used. Upload the league's blank .docx form to fill it in exactly.</span>}
              <label className="inline-flex items-center gap-1 px-3 py-1 rounded border border-border cursor-pointer text-[10px] tracking-widest uppercase hover:bg-white/5">
                <Upload className="w-3 h-3" /> {data.template?.kind === "league" ? "Replace form" : "Upload league form"}
                <input type="file" accept=".docx" className="hidden" data-testid="league-export-template-upload" onChange={async e => {
                  const f = e.target.files?.[0]; if (!f) return;
                  const fd = new FormData(); fd.append("file", f);
                  try { await api.post(`/admin/league-export/${leagueId}/template`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("League form uploaded"); load(); }
                  catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Upload failed"); }
                  e.target.value = "";
                }} />
              </label>
              {data.template?.kind === "league" && <button type="button" data-testid="league-export-template-remove" onClick={async () => { await api.delete(`/admin/league-export/${leagueId}/template`).catch(() => {}); toast.success("Removed"); load(); }} className="text-[10px] tracking-widest uppercase opacity-60 hover:opacity-100">Remove</button>}
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><Label className="text-[10px] tracking-widest uppercase opacity-70">Form title <span className="opacity-50">(plain layout only)</span></Label><Input value={cfg.title} onChange={e => setCfg({ ...cfg, title: e.target.value })} data-testid="league-export-title" /></div>
              <div><Label className="text-[10px] tracking-widest uppercase opacity-70">Subtitle <span className="opacity-50">(plain layout only)</span></Label><Input value={cfg.subtitle} onChange={e => setCfg({ ...cfg, subtitle: e.target.value })} data-testid="league-export-subtitle" /></div>
              <div><Label className="text-[10px] tracking-widest uppercase opacity-70">Club name</Label><Input value={cfg.club_name} onChange={e => setCfg({ ...cfg, club_name: e.target.value })} data-testid="league-export-club" /></div>
              <div><Label className="text-[10px] tracking-widest uppercase opacity-70">Note under the title <span className="opacity-50">(plain layout only)</span></Label><Input value={cfg.note} onChange={e => setCfg({ ...cfg, note: e.target.value })} placeholder="This information will be included in the league contacts list" data-testid="league-export-note" /></div>
            </div>
            <div>
              <Label className="text-[10px] tracking-widest uppercase opacity-70">Fields to include</Label>
              <div className="flex flex-wrap gap-2 mt-2">
                {fields.map(f => (
                  <button key={f.key} type="button" onClick={() => toggleField(f.key)} data-testid={`league-export-field-${f.key}`} className={`px-3 py-1.5 rounded border text-[10px] tracking-widest uppercase ${cfg.fields.includes(f.key) ? "border-[#83B68B] bg-[#83B68B]/15 text-[#83B68B]" : "border-border opacity-60"}`}>{f.label}</button>
                ))}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <button type="button" onClick={saveCfg} disabled={busy} data-testid="league-export-save" className="px-4 py-2 rounded border border-border text-[10px] tracking-widest uppercase inline-flex items-center gap-2"><Save className="w-3 h-3" /> Save settings</button>
              <button type="button" onClick={() => get("csv")} data-testid="league-export-csv" className="px-4 py-2 rounded border border-border text-[10px] tracking-widest uppercase inline-flex items-center gap-2"><Table className="w-3 h-3" /> Download CSV</button>
              <button type="button" onClick={() => get("docx")} data-testid="league-export-docx" className="btn-volt px-4 py-2 rounded text-[10px] tracking-widest uppercase inline-flex items-center gap-2"><FileText className="w-3 h-3" /> Download Word form</button>
            </div>
          </div>

          <div className="flex items-center gap-3 text-sm" data-testid="league-export-summary">
            <span className="font-display font-black text-2xl">{data.teams}</span><span className="opacity-60">teams in this league</span>
            {data.teams_with_gaps > 0
              ? <span className="inline-flex items-center gap-1 text-amber-300 ml-2" data-testid="league-export-gaps"><AlertTriangle className="w-4 h-4" /> {data.teams_with_gaps} with missing details</span>
              : data.teams > 0 && <span className="inline-flex items-center gap-1 text-[#83B68B] ml-2"><CheckCircle2 className="w-4 h-4" /> All complete</span>}
          </div>

          <div className="overflow-x-auto card-tactical rounded-md" data-testid="league-export-table">
            <table className="w-full text-xs">
              <thead><tr className="text-[9px] tracking-widest uppercase opacity-60 text-left">{shown.map(f => <th key={f.key} className="px-3 py-2 whitespace-nowrap">{f.label.replace(/ \(.*\)/, "")}</th>)}</tr></thead>
              <tbody>
                {data.rows.length === 0 && <tr><td colSpan={shown.length} className="px-3 py-8 text-center opacity-60">No teams are linked to this league yet — set the league on each team under Teams.</td></tr>}
                {data.rows.map(r => (
                  <tr key={r.team_id} className="border-t border-border/40" data-testid={`league-export-row-${r.team_id}`}>
                    {shown.map(f => (
                      <td key={f.key} className={`px-3 py-2 ${!r[f.key] && r.missing.includes(f.key) ? "text-amber-300" : ""}`}>{r[f.key] || (r.missing.includes(f.key) ? "— missing" : <span className="opacity-30">—</span>)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs opacity-50 inline-flex items-center gap-1"><Download className="w-3 h-3" /> Manager = the team's lead coach (name, mobile, email from their account). Ground = the team's default venue. Full-Time administrator fields are set per team under Teams → Edit.</p>
        </>
      )}
    </div>
  );
}
